import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { canRevert, type B2bChange, type DiffEntry } from "../../../../../../shared/index";
import { formatValue } from "./format-value";

/** The change feed: one row per change, expandable to the exact before/after diff. */
export function ChangeList({
  changes,
  loading,
  emptyMessage = "No changes.",
  showRevert = true,
  linkToRecord = false,
}: {
  changes: B2bChange[];
  loading?: boolean;
  emptyMessage?: string;
  /** Hide Revert where it makes no sense (e.g. the landing page's recent feed). */
  showRevert?: boolean;
  /** Show a permalink to the record — the target of the notification email's link. */
  linkToRecord?: boolean;
}) {
  if (loading) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  if (changes.length === 0)
    return <p className="p-4 text-sm text-muted-foreground">{emptyMessage}</p>;

  return (
    <ul className="divide-y">
      {changes.map((change) => (
        <ChangeRow
          key={change.id}
          change={change}
          showRevert={showRevert}
          linkToRecord={linkToRecord}
        />
      ))}
    </ul>
  );
}

function ChangeRow({
  change,
  showRevert,
  linkToRecord,
}: {
  change: B2bChange;
  showRevert: boolean;
  linkToRecord: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <li className="px-4 py-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 text-left"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{change.operation}</span>
            {change.clientKey && (
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{change.clientKey}</code>
            )}
            {change.mode !== "live" && <Badge variant="secondary">{change.mode}</Badge>}
            {change.propagation === "pending" && (
              <Badge variant="destructive">propagation pending</Badge>
            )}
            {change.notify?.status === "failed" && <Badge variant="destructive">email failed</Badge>}
            {change.revertOf && <Badge variant="outline">revert</Badge>}
            {change.revertedBy && <Badge variant="outline">reverted</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {change.summary} · {change.actor} · {new Date(change.at).toLocaleString()}
          </p>
        </div>
      </button>

      {open && (
        <div className="ml-7 mt-3 space-y-3">
          {change.mode === "dry-run" && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Dry run — this diff was computed against the real data and then rolled back.
              Nothing was committed.
            </p>
          )}

          <DiffTable diff={change.diff} />

          <div className="flex flex-wrap items-center gap-3">
            {showRevert && <RevertButton change={change} />}
            {linkToRecord && (
              <Link
                className="text-xs text-muted-foreground underline"
                to={`/dashboard/b2b/changes/${change.id}`}
              >
                Permalink
              </Link>
            )}
            {change.revertOf && (
              <Link
                className="text-xs text-muted-foreground underline"
                to={`/dashboard/b2b/changes/${change.revertOf}`}
              >
                Undoes an earlier change
              </Link>
            )}
            {change.revertedBy && (
              <Link
                className="text-xs text-muted-foreground underline"
                to={`/dashboard/b2b/changes/${change.revertedBy}`}
              >
                Undone by a later change
              </Link>
            )}
          </div>

          {change.refreshResults.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <div className="font-medium">Cache eviction</div>
              <ul className="mt-1 space-y-0.5">
                {change.refreshResults.map((result, index) => (
                  <li key={`${result.target}-${result.phase}-${index}`}>
                    {result.target} ({result.phase}):{" "}
                    <span
                      className={
                        result.status === "failed" ? "text-destructive" : "text-foreground"
                      }
                    >
                      {result.status}
                    </span>
                    {result.detail ? ` — ${result.detail}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function DiffTable({ diff }: { diff: DiffEntry[] }) {
  if (diff.length === 0) {
    return <p className="text-xs text-muted-foreground">No effective change.</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-muted-foreground">
        <tr>
          <th className="w-1/4 pb-1 text-left font-medium">Field</th>
          <th className="pb-1 text-left font-medium">Before</th>
          <th className="pb-1 text-left font-medium">After</th>
        </tr>
      </thead>
      <tbody className="align-top">
        {diff.map((entry) => (
          <tr key={entry.path} className="border-t">
            <td className="py-1 pr-2">
              <code>{entry.path}</code>
            </td>
            <td className="py-1 pr-2 text-muted-foreground">{formatValue(entry.before)}</td>
            <td className="py-1">{formatValue(entry.after)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Revert, shown only where it can actually run.
 *
 * `canRevert` is the *same* predicate the API enforces, imported from shared rather than
 * re-expressed here — a button that appears and then 409s is worse than no button, and
 * two copies of the rule drift apart the first time one of them changes.
 */
function RevertButton({ change }: { change: B2bChange }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const revert = useMutation({
    mutationFn: () => api.b2b.revertChange(change.id),
    onSuccess: async (written) => {
      await queryClient.invalidateQueries({ queryKey: ["b2b"] });
      toast.success(
        written.applied
          ? `Reverted. ${written.change.summary}`
          : `Dry run — the revert was recorded but rolled back. ${written.change.summary}`
      );
      setConfirming(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setConfirming(false);
    },
  });

  if (!canRevert(change)) return null;

  if (!confirming) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
        <Undo2 className="mr-2 h-4 w-4" />
        Revert
      </Button>
    );
  }

  return (
    <span className="flex items-center gap-2 text-xs">
      Put the client back to the &ldquo;before&rdquo; column above?
      <Button size="sm" onClick={() => revert.mutate()} disabled={revert.isPending}>
        {revert.isPending ? "Reverting…" : "Yes, revert"}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
        Cancel
      </Button>
    </span>
  );
}

